import { useMemo } from 'react'
import {
  useMinigamesServiceGetGameRecordsQuery,
  PrizeTypeEnum,
} from 'zma-react-sdk/minigames-service'
import { useTenantStore } from '@/stores/tenantStore'
import { GameRecord } from '../schema'

interface UseGameRecordsOptions {
  limit?: number
  skip?: number
  campaignId?: string
  guestPhone?: string
  prizeType?: PrizeTypeEnum
  startDate?: Date
  endDate?: Date
  userId?: string
}

export function useGameRecords(options: UseGameRecordsOptions = {}) {
  const { selectedTenantId } = useTenantStore()
  const {
    limit = 20,
    skip = 0,
    campaignId,
    guestPhone,
    prizeType,
    startDate,
    endDate,
    userId,
  } = options

  const { data, loading, error, refetch } =
    useMinigamesServiceGetGameRecordsQuery({
      variables: {
        input: {
          ...(campaignId && { campaignId }),
          ...(guestPhone && { guestPhone }),
          ...(prizeType && { prizeType }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(userId && { userId }),
        },
        pagination: {
          limit,
          skip,
        },
      },
      skip: !selectedTenantId,
      context: {
        headers: selectedTenantId ? { 'x-tenant-id': selectedTenantId } : {},
      },
    })

  const records = useMemo(() => {
    const gameRecords = data?.minigamesServiceGetGameRecords?.data || []

    // Transform GraphQL data to match our schema
    return gameRecords.map(
      (record): GameRecord => ({
        id: record.id,
        campaignId: record.campaignId,
        codeId: record.codeId || undefined,
        createdAt: record.createdAt || undefined,
        guestName: record.guestName || undefined,
        guestPhone: record.guestPhone || undefined,
        playerType: record.playerType || undefined,
        prize: record.prize
          ? {
              productId: record.prize.productId || undefined,
              type: record.prize.type,
              voucherId: record.prize.voucherId || undefined,
            }
          : undefined,
        score: record.score || undefined,
        tenantId: record.tenantId,
        updatedAt: record.updatedAt || undefined,
        userId: record.userId || undefined,
      })
    )
  }, [data])

  const total = useMemo(() => {
    return data?.minigamesServiceGetGameRecords?.total || 0
  }, [data])

  return {
    records,
    total,
    loading,
    error,
    refetch,
  }
}
